/**
 * Money-safe rounding helpers.
 *
 * Why this exists
 * ---------------
 * Plain `.toFixed(2)` and `Math.round(x * 100) / 100` can be off by one cent
 * when the underlying IEEE‑754 double is just below the true halfway value.
 *
 * Concrete bug this fixes (real example from prod):
 *
 *   totalCost  = 97.30
 *   serviceFee_raw = 97.30 * 3% = 2.919
 *   discount_raw   = 97.30 * 8% = 7.784
 *   finalCost_raw  = 97.30 + 2.919 - 7.784        // expected 92.435
 *                  ≈ 92.43499999999999            // ACTUAL double value
 *   .toFixed(2)    = "92.43"                      // wrong (expected 92.44)
 *
 * The backend computes finalCost from already-rounded fees:
 *   97.30 + 2.92 - 7.78 = 92.44   ✅
 *
 * So when the frontend POSTs its `finalCost`/`costToClient`, the backend row
 * ends up with the "92.43" the frontend computed instead of the expected 92.44.
 *
 * Always use `round2()` (or `roundMoney()`) for money rather than `.toFixed(2)`
 * or `Math.round(x * 100) / 100`. For values destined for the API, also use
 * `round2()` on the components BEFORE summing.
 */

/**
 * Round a numeric value to N decimal places using HALF_UP semantics that
 * matches Java's `BigDecimal.valueOf(value).setScale(N, HALF_UP)`.
 *
 * Adding Number.EPSILON nudges values like `92.43499999999999…` (which is
 * the actual IEEE-754 representation of mathematical `92.435`) up to the
 * correct double bucket so it rounds to `92.44`.
 *
 * @param {number|string} value
 * @param {number} [places=2]
 * @returns {number} a finite number rounded to `places` dp, or 0 for non‑finite input.
 */
export const round = (value, places = 2) => {
    const n = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(n)) return 0;
    const factor = Math.pow(10, places);
    return Math.round((n + Number.EPSILON) * factor) / factor;
};

/**
 * Convenience: round to 2 dp (cents).
 * @param {number|string} value
 * @returns {number}
 */
export const round2 = (value) => round(value, 2);

/**
 * Round and return a fixed-2-decimal string, e.g. "92.44".
 * Replaces `Number(x).toFixed(2)` in the codebase.
 * @param {number|string} value
 * @returns {string}
 */
export const money = (value) => round2(value).toFixed(2);

/**
 * Sum an array of numbers as money (each addend rounded to cents, then summed
 * and rounded again). Use this when adding several line items.
 * @param {Array<number|string>} values
 * @returns {number}
 */
export const sumMoney = (values) => round2((values || []).reduce((s, v) => s + round2(v), 0));

/**
 * Compute a percentage of a base, rounded to cents.
 * e.g. percentOf(97.30, 3) => 2.92
 * @param {number|string} base
 * @param {number|string} percent
 * @returns {number}
 */
export const percentOf = (base, percent) => round2(round2(base) * (parseFloat(percent) || 0) / 100);
